import { useEffect, useState } from "react";
import { Eye, Pencil, Download, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../../lib/api";
import { tx, useLang } from "../../../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { render, toBlob, fmtBytes, download, hasTransparency } from "./ops";

const BGS = [["checker", "Checkerboard"], ["light", "Light"], ["dark", "Dark"]];
const bgClass = (b) => (b === "checker" ? "mv-checker" : b === "light" ? "bg-white" : "bg-[#05090B]");

const BgToggle = ({ v, set, prefix }) => (
  <div className="flex gap-1">{BGS.map(([k, l]) => (
    <button key={k} onClick={() => set(k)} className={`rounded-md border px-2 py-1 text-[10px] font-semibold ${v === k ? "border-[var(--mv-teal)] text-[var(--mv-teal)]" : "border-[var(--mv-border-2)]"}`} data-testid={`${prefix}-bg-${k}`}>{tx(l)}</button>
  ))}</div>
);

const Pane = ({ label, src, info, bg, testid }) => (
  <figure className="min-w-0 flex-1" data-testid={testid}>
    <figcaption className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--mv-muted)]">{tx(label)}</figcaption>
    <div className={`flex h-72 items-center justify-center overflow-hidden rounded-xl border border-[var(--mv-border)] p-2 ${bgClass(bg)}`}>{src && <img src={src} alt="" className="max-h-full max-w-full object-contain" />}</div>
    <p className="mt-1.5 text-[11px] text-[var(--mv-dim)]" data-testid={`${testid}-info`}>{info}</p>
  </figure>
);

const PreviewDialog = ({ open, setOpen, url, info }) => { useLang();
  const [zoom, setZoom] = useState(1); const [bg, setBg] = useState("checker");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-5xl border-[var(--mv-border)] bg-[var(--mv-card)]" data-testid="studio-preview-dialog">
        <DialogHeader><DialogTitle>{tx("Finished image")} <span className="text-xs font-normal text-[var(--mv-dim)]">{info}</span></DialogTitle></DialogHeader>
        <div className="flex items-center gap-3"><BgToggle v={bg} set={setBg} prefix="studio-dialog" />
          <label className="ml-auto flex items-center gap-2 text-xs">{tx("Zoom")}<input type="range" min={0.25} max={4} step={0.25} value={zoom} onChange={(e) => setZoom(+e.target.value)} className="accent-[var(--mv-teal)]" data-testid="studio-dialog-zoom" />{Math.round(zoom * 100)}%</label>
        </div>
        <div className={`h-[65vh] overflow-auto rounded-xl border border-[var(--mv-border)] ${bgClass(bg)}`}>
          <img src={url} alt="" style={{ width: `${zoom * 100}%`, maxWidth: "none" }} className="mx-auto block" data-testid="studio-dialog-image" />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export function ResultPanel({ slot, update, onSaved }) { useLang();
  const [res, setRes] = useState(null);
  const [bg, setBg] = useState("checker");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let off = false;
    const t = setTimeout(async () => {
      try {
        const c = render(slot.current, slot.params, slot.bgImage); const blob = await toBlob(c);
        if (!off) setRes((old) => { if (old) URL.revokeObjectURL(old.url); return { canvas: c, blob, url: URL.createObjectURL(blob), w: c.width, h: c.height, alpha: hasTransparency(c) }; });
      } catch (e) { toast.error(tx("This image could not be processed")); }
    }, 300);
    return () => { off = true; clearTimeout(t); };
  }, [slot.current, slot.params, slot.bgImage]);
  const base = (slot.name || "mivo-image").replace(/\.[^.]+$/, "");
  const info = res ? `${res.w} × ${res.h}px · ${fmtBytes(res.blob.size)} · PNG${res.alpha ? ` · ${tx("transparent")}` : ""}` : tx("Rendering…");
  const webp = async () => download(await toBlob(res.canvas, "image/webp", 0.95), `${base}-mivo.webp`);
  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("finished", new File([res.blob], `${base}-mivo.png`, { type: "image/png" }));
      fd.append("original", slot.file);
      const { rotation, straighten, padding, mode, bg: b, outW, outH } = slot.params;
      fd.append("meta", JSON.stringify({ name: base, mode, ai: slot.ai, settings: { rotation, straighten, padding, outW, outH, background: b.type } }));
      const { data } = await api.post("/admin/media-library", fd);
      update({ saved: data }); toast.success(tx("Image saved successfully")); onSaved?.();
    } catch (e) { toast.error(tx(errMsg(e))); } finally { setSaving(false); }
  };
  const btn = "flex items-center justify-center gap-1.5 rounded-lg border border-[var(--mv-border-2)] px-3 py-2.5 text-xs font-bold hover:border-[var(--mv-teal)] disabled:opacity-40";
  return (
    <div className="mt-5 rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-bg)] p-4" data-testid="studio-result">
      <div className="mb-3 flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider">{tx("Original vs finished")}</span><BgToggle v={bg} set={setBg} prefix="studio-result" /></div>
      <div className="flex flex-col gap-4 sm:flex-row">
        <Pane label="Original image" src={slot.originalUrl} bg={bg} testid="studio-original" info={`${slot.meta.w} × ${slot.meta.h}px · ${fmtBytes(slot.meta.bytes)} · ${slot.meta.type}`} />
        <Pane label="Finished image" src={res?.url} bg={bg} testid="studio-finished" info={info} />
      </div>
      {slot.ai.length > 0 && <p className="mt-2 text-[11px] text-amber-300" data-testid="studio-ai-label">{tx("AI-assisted image")}: {slot.ai.map((a) => tx({ remove_bg: "background removed", ai_edit: "AI-edited", generate: "AI-generated" }[a])).join(", ")}</p>}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <button className={btn} disabled={!res} onClick={() => setOpen(true)} data-testid="studio-preview-result"><Eye className="h-3.5 w-3.5" />{tx("PREVIEW RESULT")}</button>
        <button className={btn} onClick={() => update({ stage: "edit" })} disabled={slot.stage === "edit"} data-testid="studio-edit-again"><Pencil className="h-3.5 w-3.5" />{tx("EDIT AGAIN")}</button>
        <button className={btn} disabled={!res} onClick={() => download(res.blob, `${base}-mivo.png`)} data-testid="studio-download-png"><Download className="h-3.5 w-3.5" />{tx("DOWNLOAD PNG")}</button>
        <button className={btn} disabled={!res} onClick={webp} data-testid="studio-download-webp"><Download className="h-3.5 w-3.5" />{tx("DOWNLOAD WEBP")}</button>
        <button className="mv-btn-primary col-span-2 flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-xs font-bold sm:col-span-1" disabled={!res || saving} onClick={save} data-testid="studio-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}{tx("SAVE")}
        </button>
      </div>
      <p className="mt-2 text-[11px] text-[var(--mv-dim)]">{tx("SAVE stores the image in your MIVO image library only. It never changes the homepage.")}</p>
      {res && <PreviewDialog open={open} setOpen={setOpen} url={res.url} info={info} />}
    </div>
  );
}
